/**
 * Rekap absensi bulanan — pertanyaan yang ditanya tiap akhir bulan.
 *
 * ⛔ Modul absensi berdiri sejak 11 Agustus 2026 dengan sebelas endpoint:
 * check-in mobile ber-GPS, kios ber-PIN, check-out, audit pembukaan koordinat.
 * Nol satu pun bisa menjawab *"si A masuk berapa hari?"*
 *
 * ⚠ Tabelnya bahkan punya kolom `summarized_at`, dan diukur 31 Agustus 2026:
 * NOL satu pun kode membacanya. Kolom yang menjanjikan peringkasan yang
 * mesinnya nol pernah dibangun.
 *
 * ---------------------------------------------------------------------------
 * ⛔ DUA HAL YANG DITAMPILKAN SENGAJA
 * ---------------------------------------------------------------------------
 *
 *  1. **Kolom "nol check-out".** Orang yang check-in lalu lupa check-out
 *     terlihat PERSIS SAMA dengan hari kerja normal di rekap yang cuma
 *     mencacah kehadiran — padahal jam kerjanya nol bisa dihitung. Terukur di
 *     data hari ini: 1 dari 2 karyawan.
 *
 *  2. **Hari, bukan baris.** Satu orang bisa punya beberapa baris di hari yang
 *     sama (kios lalu mobile, atau koreksi admin). Backend menghitung HARI;
 *     layar ini menyebutnya terang-terangan supaya nol ada yang mengira
 *     angkanya jumlah transaksi.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { KARYAWAN_ROLES } from "@/lib/roles";
import { apiErrorMessage } from "@/lib/inventoryApi";
import { useDaftar } from "@/hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "@/components/etams/PalangDaftar";
import RincianAbsensiGps from "@/components/etams/RincianAbsensiGps";
import Hint from "@/components/etams/Hint";

// Q35 + attendance_service.GPS_READ_ROLES: titik GPS hanya Owner + Kepala Cabang (backend yang menegakkan).
const GPS_ROLES = ["OWNER", "KEPALA_CABANG"];

const KOLOM_CSV = [
  { kunci: "kode", judul: "Kode" },
  { kunci: "nama", judul: "Karyawan" },
  { kunci: "posisi", judul: "Posisi" },
  { kunci: "hadir", judul: "Hadir" },
  { kunci: "izin", judul: "Izin" },
  { kunci: "sakit", judul: "Sakit" },
  { kunci: "cuti", judul: "Cuti" },
  { kunci: "tidak_hadir", judul: "Tidak hadir" },
  { kunci: "nol_checkout", judul: "Tanpa absen pulang" },
];

//: Cermin `URUT_SAH_REKAP` di backend/routers/hr_attendance.py.
const URUT_SAH = ["nama", "kode", "hadir", "izin", "sakit", "cuti", "tidak_hadir", "nol_checkout"];

const bulanIni = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

function Sel({ n, nada }) {
  return (
    <td className={`px-3 py-2 text-right tabular-nums ${n ? nada : "text-muted-foreground/40"}`}>
      {n || "—"}
    </td>
  );
}

export default function HRRekapAbsensi() {
  const { profile } = useAuth();
  // Bulan tinggal di alamat halaman (`?bulan=YYYY-MM`, pola daftar seragam 28 Sep 2026): tombol Kembali dari
  // rincian mengembalikan bulan yang sedang dilihat. Tidak ditulis bila = bulan ini.
  const [sp, setSp] = useSearchParams();
  const bulan = /^\d{4}-\d{2}$/.test(sp.get("bulan") || "") ? sp.get("bulan") : bulanIni();
  const setBulan = useCallback((v) => setSp((prev) => {
    const n = new URLSearchParams(prev);
    if (!v || v === bulanIni()) n.delete("bulan"); else n.set("bulan", v);
    n.delete("h");
    return n;
  }, { replace: true }), [setSp]);
  const [data, setData] = useState(null);
  const [muat, setMuat] = useState(true);
  const [buka, setBuka] = useState(null);
  const bolehGps = GPS_ROLES.includes(profile?.role?.code);
  const [galat, setGalat] = useState("");
  // POLA DAFTAR SERAGAM mode server (28 Sep 2026, DAFTAR-SERAGAM-SISA-KECIL-01 butir 3): cari, urut,
  // halaman dikerjakan server — satu panggilan tidak lagi membawa semua karyawan bulan itu.
  const daftar = useDaftar(data?.items || [], {
    server: { total: data?.total ?? 0, urutSah: URUT_SAH },
    urutAwal: { kunci: "nama", arah: "asc" },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const bangunParams = useCallback((o = {}) => ({
    bulan, ...(cari && { q: cari }), limit, offset, sort, arah, ...o,
  }), [bulan, cari, limit, offset, sort, arah]);
  const ambilHalaman = useCallback(async (off, n) =>
    (await api.get("/hr/attendance/rekap", { params: bangunParams({ offset: off, limit: n }) })).data,
  [bangunParams]);

  const muatData = useCallback(async () => {
    setMuat(true);
    setGalat("");
    try {
      const { data: d } = await api.get("/hr/attendance/rekap", { params: bangunParams() });
      setData(d);
    } catch (e) {
      setGalat(apiErrorMessage(e));
      setData(null);
    } finally {
      setMuat(false);
    }
  }, [bangunParams]);

  useEffect(() => { muatData(); }, [muatData]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            Rekap Absensi{" "}
            <Hint>Dihitung per hari, bukan per catatan. Orang yang absen dua kali di hari yang sama tetap terhitung satu hari.</Hint>
          </h1>
          {KARYAWAN_ROLES.includes(profile?.role?.code) && (
            <Link to="/hrd/karyawan" className="text-sm underline" data-testid="rekap-ke-karyawan">Kelola daftar karyawan</Link>
          )}
          {/* ⛔ Butir 2 — disebut terang-terangan (di tanda "?" judul, K200 butir 7). */}
        </div>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-foreground">Bulan</span>
          <input type="month"
                 className="rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground"
                 value={bulan} onChange={(e) => setBulan(e.target.value)} />
        </label>
      </div>

      {galat && (
        <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {galat}
        </div>
      )}

      {muat && <p className="text-sm text-muted-foreground">Menghitung…</p>}

      {!muat && data && (data.jumlah_karyawan ?? data.items.length) === 0 && (
        <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
          Belum ada catatan absensi pada {bulan} di wilayah Anda.
        </div>
      )}

      {data && (data.jumlah_karyawan ?? data.items.length) > 0 && (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <div className="p-3 pb-0">
            <PalangDaftar daftar={daftar} placeholder="Cari nama / kode karyawan…" testId="rekap-palang"
                          ekspor={{ nama: `rekap_absensi_${bulan}`, kolom: KOLOM_CSV, ambilHalaman }} />
          </div>
          <table className="w-full text-sm">
            <thead className="border-b border-border">
              <tr className="text-left">
                <ThUrut daftar={daftar} kunci="nama" className="px-3 py-2 font-medium text-foreground">Karyawan</ThUrut>
                <ThUrut daftar={daftar} kunci="hadir" className="px-3 py-2 text-right font-medium text-foreground" align="right">Hadir</ThUrut>
                <ThUrut daftar={daftar} kunci="izin" className="px-3 py-2 text-right font-medium text-foreground" align="right">Izin</ThUrut>
                <ThUrut daftar={daftar} kunci="sakit" className="px-3 py-2 text-right font-medium text-foreground" align="right">Sakit</ThUrut>
                <ThUrut daftar={daftar} kunci="cuti" className="px-3 py-2 text-right font-medium text-foreground" align="right">Cuti</ThUrut>
                <ThUrut daftar={daftar} kunci="tidak_hadir" className="px-3 py-2 text-right font-medium text-foreground" align="right">Tidak hadir</ThUrut>
                {/* ⛔ Butir 1 — kolom yang membedakan hari kerja utuh dari
                    hari yang jam kerjanya nol bisa dihitung. */}
                <ThUrut daftar={daftar} kunci="nol_checkout" className="px-3 py-2 text-right font-medium text-foreground" align="right">Tanpa absen pulang</ThUrut>
                {bolehGps && <th className="px-3 py-2" />}
              </tr>
            </thead>
            <tbody>
              {daftar.total === 0 && (
                <tr><td colSpan={bolehGps ? 8 : 7} className="px-3 py-6 text-center text-muted-foreground">Tidak ada yang cocok.</td></tr>
              )}
              {daftar.tampil.map((o) => (
                <React.Fragment key={o.employee_id}>
                <tr className="border-b border-border last:border-0">
                  <td className="px-3 py-2">
                    <span className="font-medium text-foreground">{o.nama}</span>
                    <span className="ml-2 text-xs text-muted-foreground">
                      {o.kode}{o.posisi ? ` · ${o.posisi}` : ""}
                    </span>
                  </td>
                  <Sel n={o.hadir} nada="text-success-soft-foreground" />
                  <Sel n={o.izin} nada="text-foreground" />
                  <Sel n={o.sakit} nada="text-foreground" />
                  <Sel n={o.cuti} nada="text-foreground" />
                  <Sel n={o.tidak_hadir} nada="text-danger-soft-foreground" />
                  <Sel n={o.nol_checkout} nada="text-warning-soft-foreground" />
                  {bolehGps && (
                    <td className="px-3 py-2 text-right">
                      <button type="button" className="text-xs underline" data-testid={`rekap-rincian-${o.employee_id}`}
                              onClick={() => setBuka(buka === o.employee_id ? null : o.employee_id)}>
                        {buka === o.employee_id ? "Tutup" : "Rincian & GPS"}
                      </button>
                    </td>
                  )}
                </tr>
                {bolehGps && buka === o.employee_id && (
                  <tr className="border-b border-border bg-muted/30">
                    <td colSpan={8}><RincianAbsensiGps employeeId={o.employee_id} bulan={bulan} /></td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          <Paginasi daftar={daftar} testId="rekap-paginasi" />
        </div>
      )}

      {!muat && data && (data.ada_tanpa_checkout ?? data.items.some((o) => o.nol_checkout > 0)) && (
        <p className="text-xs text-muted-foreground">
          ⚠ "Tanpa absen pulang" berarti orangnya menekan masuk tapi tidak pernah
          menekan pulang, jadi jam kerjanya tidak bisa dihitung.
        </p>
      )}
    </div>
  );
}
